import type { AxiosRequestConfig, AxiosResponse } from 'axios';
import type { User } from 'oidc-client-ts';
import React from 'react';
import type { AuthProviderProps } from 'react-oidc-context';
import { hasAuthParams, useAuth } from 'react-oidc-context';
import { getAuthAuthority, getAuthClientId, getAuthRedirectUri, getBasePath } from './env';

export const oidcConfig: AuthProviderProps = {
  authority: getAuthAuthority(),
  client_id: getAuthClientId(),
  redirect_uri: getAuthRedirectUri(),
  post_logout_redirect_uri: getBasePath(),
  onSigninCallback: (_user: User | void): void => {
    // Clean URL after OIDC redirect
    window.history.replaceState({}, document.title, window.location.pathname);
  },
};

/**
 * Hook that returns a function to execute authenticated API requests.
 * Call this at the top level of your hook/component, then use the returned
 * function inside callbacks (queryFn, mutationFn, etc.).
 *
 * @returns Function that wraps any API operation with auth headers
 *
 * @example
 * // In a query hook
 * const authRequest = useAuthenticatedRequest();
 * return useQuery({
 *   queryFn: () => authRequest(config => api.getData(config)),
 * });
 *
 * @example
 * // In a mutation hook
 * const authRequest = useAuthenticatedRequest();
 * return useMutation({
 *   mutationFn: (data) => authRequest(config => api.postData(data, config)),
 * });
 */
export function useAuthenticatedRequest() {
  const auth = useAuth();
  const [hasTriedSignin, setHasTriedSignin] = React.useState(false);
  const accessToken = auth.user?.access_token;

  return async <T>(operation: (config: AxiosRequestConfig) => Promise<AxiosResponse<T>>): Promise<AxiosResponse<T>> => {
    try {
      return await operation({
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });
    } catch (error) {
      if (!hasAuthParams() && !auth.isAuthenticated && !auth.activeNavigator && !auth.isLoading && !hasTriedSignin) {
        await auth.signinRedirect();
        setHasTriedSignin(true);
      }
      throw error;
    }
  };
}
